import React, { useState, useEffect } from "react";
import { Container, Form, Button, Row, Col, Alert, Spinner, Modal, Toast, ToastContainer } from "react-bootstrap";
import heroBg from "../assets/images/HeroBanner.png";
import { useNavigate } from "react-router-dom";
import "../styles/LicenseVerificationForm.css";

const LicenseVerificationForm = () => {
  const navigate = useNavigate();
  const [tokenState, setTokenState] = useState(localStorage.getItem("token"));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("danger");
  const [fieldErrors, setFieldErrors] = useState({});
  
  const [formData, setFormData] = useState({
    authorityName: "",
    aiotaMembershipNumber: "",
    membershipCertificate: null,
    experienceCertificate: null,
    hodLetter: null,
    acotIotrNumber: "",
    acotIotrCertificate: null,
    stateBranchName: "",
    stateBranchMembershipNumber: "",
    authority_mail_id: "",
    mode_of_certificate: "",
  });

  const handleMemberLogin = () => {
    navigate('/member-login');
  };

  const handleEdcclogin = () => { 
    navigate('/edcc-login');
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const currentToken = localStorage.getItem("token");
      if (currentToken !== tokenState){
        setTokenState(currentToken);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [tokenState]);

  const showToastMessage = (message, variant = "danger") => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
    setError(""); // Clear inline error when showing toast
  };

  const setFieldError = (fieldName, errorMessage) => {
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: errorMessage
    }));
  };

  const clearFieldError = (fieldName) => {
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  };

  const clearAllFieldErrors = () => {
    setFieldErrors({});
  };

  const handleChange = (e) => {
    const { name, value, files, type } = e.target;
    
    // Clear error when user starts typing/selecting
    if (error) setError("");
    if (showToast) setShowToast(false);
    clearFieldError(name); // Clear field-specific error when user types
    
    if (type === 'file') {
      // Handle file inputs
      if (files && files[0]) {
        // Validate file size (e.g., max 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (files[0].size > maxSize) {
          setFieldError(name, "File is too large. Maximum size is 10MB.");
          e.target.value = ''; // Clear the input
          return;
        }
        
        // Validate file type
        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(files[0].type)) {
          setFieldError(name, "Invalid file type. Please upload PDF, JPEG, JPG, or PNG files only.");
          e.target.value = ''; // Clear the input
          return;
        }
        
        setFormData((prevData) => ({
          ...prevData,
          [name]: files[0],
        }));
      }
    } else {
      // Handle text inputs
      // Real-time email validation for authority_mail_id
      if (name === 'authority_mail_id' && value.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value.trim())) {
          // Don't show toast on every keystroke, just clear invalid state
          // Toast will show on submit if invalid
        }
      }
      
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const validateForm = () => {
    clearAllFieldErrors();
    let isValid = true;

    // Validate Authority Name
    if (!formData.authorityName.trim()) {
      setFieldError("authorityName", "Authority name is required.");
      isValid = false;
    } else if (formData.authorityName.trim().length < 3) {
      setFieldError("authorityName", "Authority name must be at least 3 characters long.");
      isValid = false;
    }

    // Validate AIOTA Membership Number
    if (!formData.aiotaMembershipNumber.trim()) {
      setFieldError("aiotaMembershipNumber", "AIOTA Membership Number is required.");
      isValid = false;
    } else if (formData.aiotaMembershipNumber.trim().length < 3) {
      setFieldError("aiotaMembershipNumber", "Please enter a valid AIOTA Membership Number.");
      isValid = false;
    }

    // Validate Membership Certificate file
    if (!formData.membershipCertificate) {
      setFieldError("membershipCertificate", "AIOTA Membership Certificate is required.");
      isValid = false;
    }

    // Validate ACOT/IOTR Registration Number
    if (!formData.acotIotrNumber.trim()) {
      setFieldError("acotIotrNumber", "ACOT/IOTR Registration Number is required.");
      isValid = false;
    } else if (formData.acotIotrNumber.trim().length < 3) {
      setFieldError("acotIotrNumber", "Please enter a valid ACOT/IOTR Registration Number.");
      isValid = false;
    }

    // Validate ACOT/IOTR Certificate file
    if (!formData.acotIotrCertificate) {
      setFieldError("acotIotrCertificate", "ACOT/IOTR Certificate is required.");
      isValid = false;
    }

    // Validate State Branch Name
    if (!formData.stateBranchName.trim()) {
      setFieldError("stateBranchName", "AIOTA State Branch Name is required.");
      isValid = false;
    } else if (formData.stateBranchName.trim().length < 2) {
      setFieldError("stateBranchName", "Please enter a valid AIOTA State Branch Name.");
      isValid = false;
    }

    // Validate State Branch Membership Number
    if (!formData.stateBranchMembershipNumber.trim()) {
      setFieldError("stateBranchMembershipNumber", "AIOTA State Branch Membership Number is required.");
      isValid = false;
    } else if (formData.stateBranchMembershipNumber.trim().length < 2) {
      setFieldError("stateBranchMembershipNumber", "Please enter a valid AIOTA State Branch Membership Number.");
      isValid = false;
    }

    // Validate Mode of Certificate
    if (!formData.mode_of_certificate) {
      setFieldError("mode_of_certificate", "Mode of Certificate is required. Please select either Email or Postal.");
      isValid = false;
    } else if (formData.mode_of_certificate !== 'email' && formData.mode_of_certificate !== 'postal') {
      setFieldError("mode_of_certificate", "Invalid mode selected. Please select either Email or Postal.");
      isValid = false;
    }

    // Validate email format if authority_mail_id is provided (optional field)
    if (formData.authority_mail_id && formData.authority_mail_id.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.authority_mail_id.trim())) {
        setFieldError("authority_mail_id", "Please enter a valid email address (e.g., example@domain.com).");
        isValid = false;
      } else if (formData.authority_mail_id.trim().toLowerCase().includes('test') || 
                 formData.authority_mail_id.trim().toLowerCase().includes('example') ||
                 formData.authority_mail_id.trim().toLowerCase().includes('sample')) {
        setFieldError("authority_mail_id", "Please enter a valid authority email address. Test or example emails are not allowed.");
        isValid = false;
      }
    }

    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    setShowToast(false);

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    try {
      // Create FormData for file uploads
      const submitData = new FormData();
      
      // Append text fields
      submitData.append('authorityName', formData.authorityName);
      submitData.append('aiotaMembershipNumber', formData.aiotaMembershipNumber);
      submitData.append('acotIotrNumber', formData.acotIotrNumber);
      submitData.append('stateBranchName', formData.stateBranchName);
      submitData.append('stateBranchMembershipNumber', formData.stateBranchMembershipNumber);
      submitData.append('mode_of_certificate', formData.mode_of_certificate);
      
      // Append optional authority_mail_id only if provided
      if (formData.authority_mail_id && formData.authority_mail_id.trim()) {
        submitData.append('authority_mail_id', formData.authority_mail_id.trim());
      }
      
      // Append required files
      if (formData.membershipCertificate) {
        submitData.append('membershipCertificate', formData.membershipCertificate);
      }
      if (formData.acotIotrCertificate) {
        submitData.append('acotIotrCertificate', formData.acotIotrCertificate);
      }
      
      // Append optional files
      if (formData.experienceCertificate) {
        submitData.append('experienceCertificate', formData.experienceCertificate);
      }
      if (formData.hodLetter) {
        submitData.append('hodLetter', formData.hodLetter);
      }

      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_BASEURL}/createlicenseform`, {
        method: 'POST',
        headers: {
          // Don't set Content-Type for FormData - browser will set it automatically with boundary
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: submitData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to submit form');
      }

      if (result.success) {
        console.log("Form submitted successfully:", result.data);
        
        // Navigate to payment page instead of showing popup
        navigate('/license-verification-payment', {
          state: {
            licenseData: result.data
          }
        });
      } else {
        throw new Error(result.message || 'Form submission failed');
      }
    } catch (err) {
      console.error("Form submission error:", err);
      const errorMsg = err.message || 'An error occurred while submitting the form. Please try again.';
      setError(errorMsg);
      // Don't show toast for API errors - show in Alert instead
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    // Optionally navigate to another page
    // navigate('/dashboard');
  };

  return (
    <>
      {/* Fixed Hero Section */}
      <section
        className="hero-fixed"
        style={{ backgroundImage: `url(${heroBg})` }}>
        <div className="hero-scroll-content">
          <div className="hero-content-home poppins-regular">
            <h1 className="hero-heading">All India Occupational Therapists' Association</h1>
            {!tokenState && (
              <div className="hero-buttons">
                <button className="glass-button" onClick={handleMemberLogin}>
                  Member Login
                </button>
                <button className="glass-button" onClick={handleEdcclogin}>
                  Ergonomic Certificate login
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Scrollable Content Container */}
      <div className="scrollable-content-home">
        <div className="content-container license-verify-form-content-container">
          <section className="license-verify-form-section">
            <Container className="container-license-verify-form">
              <h2 className="text-center license-verify-form-heading mb-4">
                License Verification Form
              </h2>

              {/* Error Alert */}
              {error && (
                <Alert variant="danger" onClose={() => setError("")} dismissible>
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit} noValidate>
                <Row className="mb-3">
                  <Form.Group as={Col} controlId="authorityName">
                    <Form.Label className="form-label-license-verify-form">
                      Correct Name of Health Authority or Health Professional Council
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      className={`form-control-license-verify-form ${fieldErrors.authorityName ? 'is-invalid' : ''}`}
                      name="authorityName"
                      value={formData.authorityName}
                      onChange={handleChange}
                      placeholder="Enter authority name"
                      disabled={isSubmitting}
                    />
                    {fieldErrors.authorityName && (
                      <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                        {fieldErrors.authorityName}
                      </div>
                    )}
                  </Form.Group>
                </Row>

                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group as={Col} controlId="aiotaMembershipNumber">
                            <Form.Label className="form-label-license-verify-form">
                              AIOTA Membership Number
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                            type="text"
                            className={`form-control-license-verify-form ${fieldErrors.aiotaMembershipNumber ? 'is-invalid' : ''}`}
                            name="aiotaMembershipNumber"
                            value={formData.aiotaMembershipNumber}
                            onChange={handleChange}
                            placeholder="Enter AIOTA Membership Number"
                            disabled={isSubmitting}
                            />
                            {fieldErrors.aiotaMembershipNumber && (
                              <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                {fieldErrors.aiotaMembershipNumber}
                              </div>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group as={Col} controlId="acotIotrNumber">
                            <Form.Label className="form-label-license-verify-form">
                              ACOT/IOTR Registration Number
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                            type="text"
                            className={`form-control-license-verify-form ${fieldErrors.acotIotrNumber ? 'is-invalid' : ''}`}
                            name="acotIotrNumber"
                            value={formData.acotIotrNumber}
                            onChange={handleChange}
                            placeholder="Enter ACOT/IOTR Registration Number"
                            disabled={isSubmitting}
                            />
                            {fieldErrors.acotIotrNumber && (
                              <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                {fieldErrors.acotIotrNumber}
                              </div>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group as={Col} controlId="stateBranchName">
                            <Form.Label className="form-label-license-verify-form">
                              AIOTA State Branch Name
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                            type="text"
                            className={`form-control-license-verify-form ${fieldErrors.stateBranchName ? 'is-invalid' : ''}`}
                            name="stateBranchName"
                            value={formData.stateBranchName}
                            onChange={handleChange}
                            placeholder="Enter State Branch Name"
                            disabled={isSubmitting}
                            />
                            {fieldErrors.stateBranchName && (
                              <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                {fieldErrors.stateBranchName}
                              </div>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group as={Col} controlId="stateBranchMembershipNumber">
                            <Form.Label className="form-label-license-verify-form">
                              AIOTA State Branch Membership Number
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                            type="text"
                            className={`form-control-license-verify-form ${fieldErrors.stateBranchMembershipNumber ? 'is-invalid' : ''}`}
                            name="stateBranchMembershipNumber"
                            value={formData.stateBranchMembershipNumber}
                            onChange={handleChange}
                            placeholder="Enter State Branch Membership Number"
                            disabled={isSubmitting}
                            />
                            {fieldErrors.stateBranchMembershipNumber && (
                              <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                {fieldErrors.stateBranchMembershipNumber}
                              </div>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group as={Col} controlId="mode_of_certificate">
                      <Form.Label className="form-label-license-verify-form">
                        Mode of Certificate
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <div className="d-flex gap-4 mt-2">
                        <Form.Check
                          type="radio"
                          id="mode_email"
                          name="mode_of_certificate"
                          value="email"
                          checked={formData.mode_of_certificate === 'email'}
                          onChange={handleChange}
                          label="Email"
                          disabled={isSubmitting}
                        />
                        <Form.Check
                          type="radio"
                          id="mode_postal"
                          name="mode_of_certificate"
                          value="postal"
                          checked={formData.mode_of_certificate === 'postal'}
                          onChange={handleChange}
                          label="Postal"
                          disabled={isSubmitting}
                        />
                      </div>
                      {fieldErrors.mode_of_certificate && (
                        <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {fieldErrors.mode_of_certificate}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group as={Col} controlId="authority_mail_id">
                      <Form.Label className="form-label-license-verify-form">
                        Authority Mail ID
                      </Form.Label>
                      <Form.Control
                        type="text"
                        className={`form-control-license-verify-form ${fieldErrors.authority_mail_id ? 'is-invalid' : ''}`}
                        name="authority_mail_id"
                        value={formData.authority_mail_id}
                        onChange={handleChange}
                        placeholder="Enter authority email address"
                        disabled={isSubmitting}
                      />
                      {fieldErrors.authority_mail_id && (
                        <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {fieldErrors.authority_mail_id}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group as={Col} controlId="membershipCertificate">
                            <Form.Label className="form-label-license-verify-form">
                              AIOTA Membership Certificate Copy
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                            type="file"
                            className={`file-input-license-verify-form ${fieldErrors.membershipCertificate ? 'is-invalid' : ''}`}
                            name="membershipCertificate"
                            onChange={handleChange}
                            accept="application/pdf,image/*"
                            disabled={isSubmitting}
                            />
                            {fieldErrors.membershipCertificate && (
                              <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                {fieldErrors.membershipCertificate}
                              </div>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group as={Col} controlId="acotIotrCertificate">
                            <Form.Label className="form-label-license-verify-form">
                              ACOT/IOTR Certificate Copy
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                            type="file"
                            className={`file-input-license-verify-form ${fieldErrors.acotIotrCertificate ? 'is-invalid' : ''}`}
                            name="acotIotrCertificate"
                            onChange={handleChange}
                            accept="application/pdf,image/*"
                            disabled={isSubmitting}
                            />
                            {fieldErrors.acotIotrCertificate && (
                              <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                {fieldErrors.acotIotrCertificate}
                              </div>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group as={Col} controlId="experienceCertificate">
                            <Form.Label className="form-label-license-verify-form">
                              Experience Certificate (for working professionals)
                            </Form.Label>
                            <Form.Control
                            type="file"
                            className="file-input-license-verify-form"
                            name="experienceCertificate"
                            onChange={handleChange}
                            accept="application/pdf,image/*"
                            disabled={isSubmitting}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group as={Col} controlId="hodLetter">
                            <Form.Label className="form-label-license-verify-form">
                              Letter from HOD (if you are a student)
                            </Form.Label>
                            <Form.Control
                            type="file"
                            className="file-input-license-verify-form"
                            name="hodLetter"
                            onChange={handleChange}
                            accept="application/pdf,image/*"
                            disabled={isSubmitting}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="d-flex justify-content-center mt-5">
                  <Button 
                    variant="primary" 
                    type="submit" 
                    className="w-auto d-inline-flex align-items-center justify-content-center"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                       <span
      className="d-inline-flex align-items-center justify-content-center"
      style={{ lineHeight: 1 }}
    >
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
  style={{
          width: "14px",
          height: "14px",
          borderWidth: "2px",
        }}                        />
                        Submitting...
                      </span>
                    ) : (
                      'Submit'
                    )}
                  </Button>
                </Row>
              </Form>
            </Container>
          </section>
        </div>
      </div>

      {/* Toast Notification */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast 
          show={showToast} 
          onClose={() => setShowToast(false)} 
          delay={5000} 
          autohide
          bg={toastVariant}
        >
          <Toast.Header>
            <strong className="me-auto">
              {toastVariant === "danger" ? "Error" : "Success"}
            </strong>
          </Toast.Header>
          <Toast.Body className={toastVariant === "danger" ? "text-white" : ""}>
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Success Modal */}
      <Modal show={showPopup} onHide={handleClosePopup} centered>
        <Modal.Header closeButton>
          <Modal.Title>Success!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-3">
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="10" fill="#28a745" />
                <path
                  d="M8 12.5L10.5 15L16 9"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h5>Form Submitted Successfully!</h5>
            <p className="text-muted">
              Your license verification form has been submitted successfully. 
              We will process your request shortly.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleClosePopup}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LicenseVerificationForm;